// ============================================================================
// Audio Library — a separate category of practice from the 13 guided
// interventions. Each entry is a single MP3 with bilateral pan effects baked
// in. Users tap a card to open a stripped-down player page.
//
// To add a new practice:
//   1. Drop the .mp3 into /app/frontend/public/audio/library/
//   2. Append an entry below with the matching `audioFile` path
// ============================================================================

export const LIBRARY_PRACTICES = [
  {
    id: "mindfulness-of-breath",
    title: "Mindfulness of Breath",
    duration: "8 min",
    category: "Guided Meditation",
    description:
      "A short guided meditation that anchors your attention to the natural breath. Soft pauses give you space to settle, with a gentle reminder that wandering thoughts are part of the practice.",
    audioFile: "/audio/meditations/mindfulness-of-breath.mp3",
    image: "/images/library/mindfulness-of-breath.webp",
    imagePosition: "center 35%",
    freePreview: false,
    bilateralCapable: false,
    // Pure-narration meditation. When `script` is present the player
    // requests a stitched TTS MP3 from /api/narration/meditation
    // instead of loading the static audioFile. Pause markers
    // `[Pause for Xs]` or `[Pause for X seconds]` insert silent gaps
    // of X seconds between sentences.
    script: `Begin by finding a posture that feels both supported and comfortable. Rest your hands gently on your thighs or in your lap. If you are lying down, let your arms rest where they are most at ease. Allow your eyes to soften or fully close.

Take a deep, cleansing breath in through your nose... and let it go with a long, slow sigh through your mouth.

Do this one more time. Inhale deeply... and exhale completely, letting go of any tension you are holding in your shoulders, your jaw, or your face.

Bring your awareness into the physical space you are occupying. Feel the weight of your body pressing down into the chair, the bed, or the floor. Connect with the sensation of gravity holding you safely in place. You have nowhere else to go right now, and nothing else you need to do. Just be here, exactly as you are.

[Pause for 15 seconds]

Now, turn your attention inward toward your natural breath. Do not try to control it. Do not try to change it. Simply notice it. Where do you feel the breath most clearly in your body right now?

Perhaps it is the cool sensation of air entering your nostrils. Perhaps it is the gentle rise and fall of your chest. Or maybe you feel it most deeply in the expansion and softening of your abdomen.

Choose one spot where the breath feels most vivid to you. This spot will be your anchor. Anchor your awareness right there, watching the full cycle of one inhalation... and the full cycle of one exhalation.

[Pause for 30 seconds]

As you stay with your anchor, notice the tiny details of your breathing. Notice the brief stillness at the top of the inhale, just before it turns into an exhale. Notice the quiet pause at the bottom of the exhale, just before the next breath begins.

Ride these waves of breathing. Let the breath breathe itself.

If your mind feels restless, imagine your breath as an anchor holding a boat steady in an ocean storm. Thoughts may pass by like waves, but you remain anchored directly to this present moment.

[Pause for 60 seconds]

It is the natural habit of the mind to wander. You may notice thoughts, memories, plans, or physical sounds pulling you away.

Whenever you catch your mind drifting, remember that nothing has gone wrong. The exact moment you notice your mind wandering is a moment of mindfulness.

Gently, patiently, and without any self-judgment, acknowledge where your mind went. Then, softly guide your awareness right back to your anchor. Back to the cool air, the rising chest, or the moving belly.

Rest here again, in silence, one breath at a time.

[Pause for 60 seconds]

As we bring this breath practice to a close, take a moment to notice how you feel. Acknowledge yourself for taking this time to quiet your mind and connect with your body.

Slowly expand your awareness outside of yourself. Feel the air on your skin. Listen to the faint sounds in the room around you.

Gently begin to wiggle your fingers and your toes. Take one more deep, conscious breath in... and let it go. When you feel ready, softly open your eyes and return to the space around you.`,
  },
  {
    id: "mindful-body-scan",
    title: "Mindful Body Scan",
    duration: "11 min",
    category: "Guided Meditation",
    description:
      "A slow, head-to-toe body scan inviting curious, non-judgmental awareness to each region of the body. Ideal lying down — fall asleep is welcome.",
    audioFile: "/audio/meditations/mindful-body-scan.mp3",
    image: "/images/library/mindful-body-scan.webp",
    imagePosition: "center 25%",
    freePreview: false,
    bilateralCapable: false,
    script: `Begin by finding a comfortable position. It is often best to lie down flat on your back for a body scan, but a supported sitting position works well too. Allow your arms to rest loosely by your sides, palms facing up. Let your feet fall open naturally.

Gently close your eyes. Take a deep, slow breath in through your nose, expanding your chest and belly... and exhale fully through your mouth, letting your entire body sink heavily into the surface beneath you.

The intention of this practice is not to change how you feel, or to force relaxation. The goal is simply to bring a non-judgmental, curious awareness to each part of your body as we move through it.

If you fall asleep, that is okay. If your mind wanders, that is okay too. Whenever you notice your mind drifting, gently guide your focus back to the part of the body we are exploring.

[Pause for 15 seconds]

Bring your attention all the way down to your feet. Focus your awareness on the toes of both feet. Notice any sensations here — tingling, warmth, coolness, or tightness.

Move your awareness to the soles of your feet, the heels, and the tops of your feet. Feel the ankles. Just notice whatever is present, even if it is a lack of sensation.

Now, let your attention move up into your lower legs. Feel your calves, your shins, and the structure of your shins. Move up into your kneecaps and the soft space behind your knees.

Continue moving up into your upper legs. Feel the large muscles of your thighs and the hamstrings. Notice the weight of your thighs resting against the surface below. Let the muscles soften and release.

[Pause for 45 seconds]

Shift your awareness into your pelvic region, your hips, and your glutes. Notice the sensations of weight and contact.

Bring your attention to your lower back and your abdomen. Notice the gentle, rhythmic rise and fall of your stomach with each breath. Feel your inner body releasing.

Move your focus up into your mid-back, upper back, and your chest. Feel the expansion of your ribcage with every inhalation, and the contraction with every exhalation. Notice the beat of your heart from the inside out.

If you feel any tightness or tension in your back or chest, imagine sending your breath directly into those spaces, allowing them to open and soften.

[Pause for 60 seconds]

From your chest, let your awareness stream outward into both of your shoulders. The shoulders often hold onto stress. Consciously invite them to drop and broaden.

Let your focus travel down both arms simultaneously. Move past the upper arms, the elbows, and into the forearms. Feel your wrists.

Bring your attention into your hands. Feel the palms of your hands, the backs of your hands, and each individual finger, all the way to the fingertips. Notice any buzzing, pulsing, or air moving across your skin. Rest your awareness in the hands.

[Pause for 45 seconds]

Bring your focus back up through your arms and rest it at your neck and throat. Notice any swallowing or tightness.

Move your awareness into your face. Soften your jaw, letting your teeth part slightly. Relax your tongue. Smooth out the muscles around your mouth, your cheeks, and your nose.

Let your eyes settle deeply into their sockets. Relax the space between your eyebrows. Smooth out your forehead. Move your awareness to the sides of your head, the back of your head, and finally to the very top of your head. Let your scalp relax completely.

[Pause for 30 seconds]

Now, expand your awareness so that it encompasses your entire physical form, from the tips of your toes to the top of your head. Feel your body as one unified, breathing whole. Rest in this complete presence.

[Pause for 30 seconds]

Slowly, begin to deepen your breath. Bring gentle movement back into your physical space by wiggling your fingers and your toes. Circle your wrists and ankles.

Take a gentle stretch if your body asks for it. When you feel fully present and ready, slowly open your eyes, carrying this sense of embodied awareness into the rest of your day.`,
  },
  {
    id: "mindfulness-of-inner-sensations",
    title: "Mindfulness of Inner Sensations",
    duration: "9 min",
    category: "Guided Meditation",
    description:
      "Turn awareness inward — to temperature, tingling, the heartbeat, and the quiet movement of being alive. Soften around discomfort and rest in the direct experience of your body.",
    audioFile: "/audio/meditations/mindfulness-of-inner-sensations.mp3",
    image: "/images/library/inner-sensations.webp",
    imagePosition: "center 40%",
    freePreview: false,
    bilateralCapable: false,
    script: `Begin by finding a posture that feels comfortable, stable, and at ease. Rest your hands gently on your lap. If you are lying down, let your arms rest where they are most at ease. Allow your eyes to soften or completely close.

Take a deep, full breath in... and let it out with a slow, heavy sigh. Do this once more. Inhale deeply... and as you exhale, feel your shoulders drop away from your ears. Let go of any tension in your jaw and forehead.

Bring your attention to the physical points of contact between your body and the surface beneath you. Feel your feet on the floor. Feel your legs and hips resting on the chair or cushion. Let yourself be fully supported by the earth.

Turn your gaze inward, shifting your focus from the world outside to the world within your own body.

[Pause for 15 seconds]

Begin to notice the overall, large sensations present in your body right now.

Scan your physical frame and notice the sensation of temperature. Is the body warm, cool, or neutral? Notice how the air feels on your exposed skin compared to the parts of your body covered by clothing.

Notice any areas of obvious tightness, pressure, or heaviness. Perhaps there is tension in the neck or a weight in the shoulders. Do not try to change these sensations or fix them. Simply acknowledge them with curiosity. Treat them like weather passing through the sky of your body.

[Pause for 40 seconds]

Now, let your awareness dive a bit deeper into the internal landscape of your body. Look for the more subtle, quiet sensations.

Can you feel the gentle thrum or tingling in the palms of your hands or the tips of your fingers? Can you feel a pulsing or vibration in your feet or legs?

Tune in closely to the center of your chest or your throat. See if you can detect the faint, rhythmic beat of your own heart. Feel the internal movement of life flowing through you.

Let your attention rest gently on whichever inner sensation feels most vivid right now. Let that sensation be your anchor.

[Pause for 60 seconds]

As you stay with your inner sensations, you may encounter areas of physical discomfort, restlessness, or emotional holding.

Instead of turning away from discomfort, see if you can soften around it. Imagine breathing directly into that sensation, creating space around it.

If your mind wanders into thoughts, stories, or judgments about what you are feeling, that is perfectly normal. As soon as you notice the mind has drifted, gently bring your focus back to the raw physical feeling itself. Is it tight? Is it buzzing? Is it spacious? Rest here in the direct experience of your body.

[Pause for 60 seconds]

Slowly begin to let go of any focus on specific sensations. Allow your awareness to widen, holding your entire body as one complete whole. Feel the unity of your breath, your heartbeat, and your presence.

Take a moment to offer appreciation to your body for its wisdom and the constant work it does to keep you alive and balanced.

Gradually, begin to bring your awareness back to the room. Feel the weight of your body on the chair. Listen to the sounds around you. Gently wiggle your fingers and toes to bring movement back to the surface. Take one final, deep breath in... and let it go. When you are ready, gently open your eyes.`,
  },
  {
    id: "ocean-waves",
    title: "Ocean Waves (Bilateral Stereo)",
    duration: "61 min",
    category: "Calming Audio",
    description:
      "Gentle ocean waves rolling across the beach with bilateral option to further calm your mind. Pairs beautifully with headphones for full stereo immersion.",
    audioFile: "/audio/library/ocean-waves.mp3",
    image:
      "https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=800&auto=format&fit=crop",
    freePreview: true,
    bilateralCapable: true,
    bilateralBpm: 40,
  },
  {
    id: "forest-tones",
    title: "Forest Tones — 40 Hz",
    duration: "15 min",
    category: "Cognitive Health",
    description:
      "A 15-minute forest soundscape layered with 40 Hz gamma-frequency binaural beats, gently panned across the stereo field. Inspired by ongoing research into 40 Hz sensory stimulation — known as GENUS — and its observed effects on neural rhythms tied to memory and cognitive resilience. Best used with headphones.",
    audioFile: "/audio/library/forest-tones.mp3",
    image:
      "https://images.unsplash.com/photo-1448375240586-882707db888b?w=800&auto=format&fit=crop",
    freePreview: false,
    bilateralCapable: true,
    bilateralBpm: 40,
  },
  {
    id: "deep-calm-432hz",
    title: "Deep Calm — 432Hz Pan",
    duration: "60 min",
    category: "Deep Rest",
    description:
      "A 60-minute 432Hz ambient loop that pans left to right, designed for restorative rest.",
    audioFile: "/audio/library/deep-calm-432hz.mp3",
    image:
      "https://images.unsplash.com/photo-1419242902214-272b3f66ee7a?w=800&h=600&fit=crop&auto=format",
    freePreview: false,
    bilateralCapable: true,
    bilateralBpm: 40,
  },
  {
    id: "gamma-40hz",
    title: "Memory & Focus — 40 Hz",
    duration: "60 min",
    category: "Cognitive Health",
    description:
      "A 60-minute 40 Hz gamma-frequency tone. This one sounds strange... but it's rooted in *cutting-edge* research in memory and 40 Hz gamma sound at MIT. Ongoing research into 40 Hz sensory stimulation — known as GENUS — is observed to have positive effects on improved memory and cognitive resilience. Best used with headphones.",
    audioFile: "/audio/library/gamma-40hz.mp3",
    image: "/images/library/purple-brain.webp",
    freePreview: false,
  },
];

export function getLibraryPractice(id) {
  return LIBRARY_PRACTICES.find((p) => p.id === id) || null;
}

// Lightweight inline-italic renderer: turns `*phrase*` into <em>phrase</em>.
// Returns an array of React-safe nodes (strings + <em> elements).
export function renderInlineItalic(text) {
  if (!text) return text;
  const parts = text.split(/(\*[^*]+\*)/g);
  return parts.map((part, i) =>
    /^\*[^*]+\*$/.test(part)
      ? { italic: true, key: i, value: part.slice(1, -1) }
      : { italic: false, key: i, value: part }
  );
}
